import dayjs from "dayjs"
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"

import { Button } from "components/Button"
import { GenericModal } from "components/Modal/Modal"
import PreviousButton from "components/Previous/PreviousButton"
import Spacer from "components/Spacer"
import PharmaSupportContact from "components/PharmaSupportContact/PharmaSupportContact"
import { SecurityToInsertReferent } from "containers/InsertCarer/InsertProfessionalCarer"
import PharmacyOwnerInfo from "containers/PharmacyOwnerInfo/PharmacyOwnerInfo"
import TeleexpertiseMenu from "containers/PharmacyOwnerInfo/TeleexpertiseMenu"
import MssSettings, { MssSettingsStep } from "containers/PharmacySettings/MssSettings"
import SettingsMenu from "containers/PharmacySettings/SettingsMenu"
import { useDirectoryMss } from "containers/PharmacySettings/useDirectoryMss"
import { cdn } from "core/cdn"
import {
  ADMIN_CARERS_PAGE,
  DIAGNOSTIC_DEVICES_PAGE,
  PROFILE_PAGE,
  READERS_CONFIGURATION_PAGE,
  TUTORIALS_PAGE,
} from "core/constants"
import { ClientStore } from "types/store"

import styles from "./Home.module.scss"
import useUpdator from "./hooks/useUpdator"
import { useLanguage } from "locales"
import { Message } from "types/redux"
import { request } from "lib/request"

export const HOME_PHARMA_DIAGNOSTIC_NAV_STATE = {
  fromHomePharmaDiagnostic: true,
} as const

const TELEEXPERTISE_MODAL_SIZE = { width: 1600, height: 850 }

// "Autres réglages" screens shown inside the "Espace professionnel" modal
type SettingsView = "verify" | "settings" | "mss" | "teleexpertise" | "ownerInfo"

const FailedStatsSend = () => {
  const { languages } = useLanguage(); 
  return (
    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px'}}><img src={cdn("images/mail_failure.svg")} alt="mail_newsletter_fail" /><span style={{marginTop: '40px', paddingBottom: '40px', fontSize: '48px', fontWeight: 'bold'}}>{languages.statisticsSendWithFailure}</span></div>
  )
}

const SuccessStatsSend = () => {
  const { languages } = useLanguage(); 
  return (
    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px'}}><img src={cdn("images/mail_success.svg")} alt="mail_newsletter_success" /><span style={{marginTop: '40px', paddingBottom: '40px', fontSize: '48px', fontWeight: 'bold'}}>{languages.statisticsSendWithSuccess}</span></div>
  )
}
const Home: React.FC<unknown> = () => {
  const { languages, currentLang } = useLanguage()
  // Update REACT_APP
  const { version, needToUpdate } = useUpdator()
  const [displayedVersion, setDisplayedVersion] = useState<string | null>(null)
  const [statisticsState, setShowStatisticsState] = useState<'success' | 'fail' | null>(null);
  if (needToUpdate) window.location.reload()
  ///////////////////////////////////////////
  const [modalPharmaAccountVisible, setModalPharmaAccountVisible] =
    useState(false)
  const [securedNavigationFor, setSecuredNavigationFor] = useState(null)
  // "Autres réglages" screens shown inside the "Espace professionnel" modal
  const [teleexpertiseView, setTeleexpertiseView] = useState<SettingsView | null>(null)
  // The MSS screen can be reached from the menu or from "Informations du titulaire"
  const [mssReturnTo, setMssReturnTo] = useState<SettingsView>("settings")
  const teleexpertiseHasOwnBack = teleexpertiseView !== null && teleexpertiseView !== "verify"
  // The screens after the email check use the same fixed popup size, except the MSS address
  // entry which, like the email check, needs room for its keyboard
  const [mssStep, setMssStep] = useState<MssSettingsStep>("main")
  // Set when a new owner was just saved: the MSS screen opens on the address entry
  const [mssConfirmNewOwner, setMssConfirmNewOwner] = useState(false)
  const [ownerInfoNotice, setOwnerInfoNotice] = useState<Message | null>(null)
  const teleexpertiseFixedSize =
    teleexpertiseHasOwnBack && !(teleexpertiseView === "mss" && mssStep === "entry")
  const teleexpertiseContentRef = useRef<HTMLDivElement>(null)
  const [teleexpertiseHeight, setTeleexpertiseHeight] = useState<number | undefined>()
  // Shared by the "Fermer" button and a tap on the mask, from any view of the modal
  const closePharmaAccountModal = () => {
    setModalPharmaAccountVisible(false)
    setShowStatisticsState(null)
    setSecuredNavigationFor(null)
    setTeleexpertiseView(null)
    setMssReturnTo("settings")
    setMssStep("main")
    setMssConfirmNewOwner(false)
    setOwnerInfoNotice(null)
  }

  useLayoutEffect(() => {
    const wrapper = teleexpertiseContentRef.current
    const modalContent = wrapper?.closest(".ant-modal-content") as HTMLElement | null
    if (!wrapper || !modalContent || !teleexpertiseFixedSize) return
    // Everything around the wrapper (header, paddings) stays constant for a given view
    const chrome = modalContent.offsetHeight - wrapper.offsetHeight
    setTeleexpertiseHeight(TELEEXPERTISE_MODAL_SIZE.height - chrome)
  }, [teleexpertiseView, teleexpertiseFixedSize])

  const [showAccessibility, setShowAccessibility] = useState(false);
  const navigate = useNavigate()

  const { admin } = useSelector(({ client }: { client: ClientStore }) => ({
    admin: client.admin,
  }))
  // One directory lookup per "Autres réglages" session, shared by the MSS and owner screens
  const directoryMss = useDirectoryMss(admin, teleexpertiseView !== null)

  useEffect(() => {
    if (version) {
      const datify = dayjs(version)
      if (datify.isValid()) setDisplayedVersion(datify.format("DD.MM.YYYY"))
      else setDisplayedVersion(version)
    }
  }, [version])

  return (
    <>
      {admin && (
        <div
          className={styles.PharmaAccount}
          onClick={() => {
            setModalPharmaAccountVisible(true)
          }}
        >
          <img
            src={cdn("/icons/profil_pharma.svg")}
            height={30}
            alt={languages.yourPharmaAccount}
          />
          <span className={styles.ConnectedAs}>{admin?.name}</span>
        </div>
      )}
      <div className={styles.LayoutHome} onClick={() => navigate(PROFILE_PAGE)}>
        <div className={styles.TaglineInfo}>
          <img src={cdn("images/medadom_blue.png")} height={60} alt="" />
          <img
            src={cdn("images/medadom_logo_blue.svg")}
            height={60}
            alt="medadom logo"
            style={{ marginLeft: "15px" }}
          />
        </div>
        <span className={styles.TouchScreen}>{languages.touch_screen}</span>
        <div className={styles.ClickAnimationFrame}>
          <video
            className={styles.ClickAnimation}
            src={cdn("/videos/animation_clic.webm")}
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
          />
        </div>
        <div className={styles.CampaignIllustration}>
          <img
            className={styles.HomepageImage}
            src={cdn(currentLang === "en" ? "/images/ruban_rose_en.svg" : "/images/ruban_rose.svg")}
            alt={languages.pinkOctoberCampaign}
          />
        </div>
      </div>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <div className={styles.AccessibilityLink}><u><a onClick={() => setShowAccessibility(true)}>{languages.accessibility_declaration}</a></u></div>
      {displayedVersion && (
        <div className={styles.Versioning}>V. {displayedVersion}</div>
      )}
      <GenericModal
        title={
          statisticsState
            ? languages.statistics
            : securedNavigationFor || teleexpertiseView
                ? null
                : languages.modal_pharmaAccount_title
        }
        noFooter
        style={teleexpertiseFixedSize ? { width: TELEEXPERTISE_MODAL_SIZE.width } : undefined}
        closable={!teleexpertiseHasOwnBack}
        onClose={closePharmaAccountModal}
        onMaskClick={closePharmaAccountModal}
        visible={modalPharmaAccountVisible}
      >
        {['fail', 'success'].includes(statisticsState) ? statisticsState === 'success' ? <SuccessStatsSend /> : <FailedStatsSend /> : teleexpertiseView ? (
          <div
            ref={teleexpertiseContentRef}
            className={styles.TeleexpertiseContent}
            style={{ height: teleexpertiseFixedSize ? teleexpertiseHeight : undefined }}
          >
            {teleexpertiseView === "verify" ? (
              <SecurityToInsertReferent
                onFinish={() => setTeleexpertiseView("settings")}
                onCancel={() => setTeleexpertiseView(null)}
              />
            ) : teleexpertiseView === "settings" ? (
              <SettingsMenu
                onBack={() => setTeleexpertiseView(null)}
                onMss={() => {
                  setMssReturnTo("settings")
                  setMssConfirmNewOwner(false)
                  setTeleexpertiseView("mss")
                }}
                onTeleexpertise={() => setTeleexpertiseView("teleexpertise")}
              />
            ) : teleexpertiseView === "mss" ? (
              <MssSettings
                directory={directoryMss}
                confirmNewOwner={mssConfirmNewOwner}
                onBack={(saved) => {
                  setOwnerInfoNotice(saved ?? null)
                  setTeleexpertiseView(mssReturnTo)
                }}
                onStepChange={setMssStep}
              />
            ) : teleexpertiseView === "teleexpertise" ? (
              <TeleexpertiseMenu
                onBack={() => setTeleexpertiseView("settings")}
                onOwnerInfo={() => setTeleexpertiseView("ownerInfo")}
              />
            ) : (
              <PharmacyOwnerInfo
                directory={directoryMss}
                initialNotice={ownerInfoNotice}
                onBack={() => {
                  setOwnerInfoNotice(null)
                  setTeleexpertiseView("teleexpertise")
                }}
                onEditMss={() => {
                  setOwnerInfoNotice(null)
                  setMssReturnTo("ownerInfo")
                  setMssConfirmNewOwner(false)
                  setTeleexpertiseView("mss")
                }}
                onOwnerChanged={() => {
                  setOwnerInfoNotice(null)
                  setMssReturnTo("ownerInfo")
                  setMssConfirmNewOwner(true)
                  setTeleexpertiseView("mss")
                }}
              />
            )}
          </div>
        ) : securedNavigationFor ? (
          <SecurityToInsertReferent
            onFinish={() => {
              navigate(
                securedNavigationFor,
                securedNavigationFor === DIAGNOSTIC_DEVICES_PAGE
                  ? { state: HOME_PHARMA_DIAGNOSTIC_NAV_STATE }
                  : undefined,
              )
            }}
            onCancel={() => setSecuredNavigationFor(null)}
          />
        ) : (
          <Spacer direction="vertical">
            <div className={styles.ButtonsGridFirstRow}>
              <Button
                className={styles.ButtonStyleFirstRow}
                onClick={
                  () => navigate(TUTORIALS_PAGE)
                }
              >
                {languages.myTutorialVideos.toUpperCase()}
              </Button>
              <Button
                className={styles.ButtonStyleFirstRow}
                onClick={() => {
                  setSecuredNavigationFor(ADMIN_CARERS_PAGE)
                }}
              >
                {languages.companion.toUpperCase()}
              </Button>
              <Button
                className={styles.ButtonStyleFirstRow}
                onClick={() => {
                request('/pharmacies/statistics/send', {
                  method: 'POST',
                }).then((res : any) => {
                  if(res?.status === 'ok') {
                    setShowStatisticsState('success');
                  } else {
                    setShowStatisticsState('fail');
                  }
                })
              }}
              >
                {languages.myStatistics.toUpperCase()}
              </Button>                
              </div>

              <div className={styles.ButtonsGridSecondRow}>
                <Button
                  className={styles.ButtonStyleFirstRow}
                  onClick={() => {
                    navigate(DIAGNOSTIC_DEVICES_PAGE, { state: HOME_PHARMA_DIAGNOSTIC_NAV_STATE })
                  }}
                >
                  {languages.technical_diagnostic.toUpperCase()}
                </Button>
                <Button
                  className={styles.ButtonStyleFirstRow}
                  onClick={() => {
                    setSecuredNavigationFor(READERS_CONFIGURATION_PAGE)
                  }}
                >
                  {languages.associateKiosk.toUpperCase()}
                </Button>
                <Button
                  className={styles.ButtonStyleFirstRow}
                  onClick={() => setTeleexpertiseView("verify")}
                >
                  {languages.otherSettingsCta.toUpperCase()}
                </Button>
              </div>
              <PharmaSupportContact />
          </Spacer>
        )}
      </GenericModal>
      {showAccessibility &&
        <GenericModal visible={showAccessibility} footer={undefined} bodyStyle={{height: 600}}>
          <div>
            <div style={{textAlign: "right"}} onClick={() => setShowAccessibility(false)}>
            <PreviousButton
              text={languages.previous}
              onClick={() => setShowAccessibility(false)}
            />          </div>
            <div style={{marginTop: "50px"}}>
            <iframe src={`https://info.medadom.com/declaration-accessibilite-kiosk`} width="1700px" height="600px" title="Déclaration d'accessibilité"/>
            </div>
          </div>
        </GenericModal>
      }
    </>
  )
}

export default Home
